import React, { useState, useEffect, useCallback } from 'react';
import { App } from 'antd';
import { logger } from '../services/logger.client';
import {
  ConfirmationContext,
  confirmModal as confirmModalBridge,
  type ConfirmOptions,
} from './ConfirmationContext';

export const ConfirmationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { modal } = App.useApp();
  const [isAuthConfirming, setIsAuthConfirming] = useState(false);

  useEffect(() => {
    logger.info({ hasModal: !!modal }, 'ConfirmationProvider has mounted.');
  }, [modal]);

  // Create a bridge for legacy JS to open the modal
  useEffect(() => {
    const showConfirm = (opts: ConfirmOptions) => {
      if (opts.isAuthModal) {
        logger.info('Auth-related confirmation modal is being shown.');
        setIsAuthConfirming(true);
      }
      modal.confirm({
        title: opts.title ?? 'Confirm Action',
        content: opts.message,
        onOk: () => {
          logger.info('Confirmation modal OK clicked.');
          opts.onConfirm();
        },
        okText: opts.okText ?? 'Confirm',
        okType: 'danger',
        cancelText: opts.cancelText ?? 'Cancel',
        zIndex: 1050,
        afterClose: () => {
          if (opts.isAuthModal) {
            logger.info('Auth-related confirmation modal has closed.');
            setIsAuthConfirming(false);
          }
        },
      });
    };

    // Assign to the legacy bridge
    confirmModalBridge.current = showConfirm;
  }, [modal]);

  // Stable wrapper function that always calls the current bridge function.
  // This ensures consumers always get the latest function, even if they
  // consumed the context before the useEffect ran.
  const confirm = useCallback((opts: ConfirmOptions) => {
    confirmModalBridge.current(opts);
  }, []);

  return (
    <ConfirmationContext.Provider value={{ confirm, isAuthConfirming }}>
      {children}
    </ConfirmationContext.Provider>
  );
};